import { Component, inject, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    CONFIRMATION_TYPES,
    DashboardButtonComponent,
    DashboardButtonModel,
    DisplayDecorator,
    EscalationComponent,
    EscalationInstanceToken,
    EscalationInterface,
    EventService,
    isDefined,
    isNgRxEnabled,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationModel,
    notificationRefreshRequired,
    NotificationStatusInstanceToken,
    NotificationUpdateService,
    SoftwareLicencesInstanceToken,
    SoftwareLicencesInterface,
    StatusModel,
    SynchronizationService,
    TextModel,
    TravelStatusUpdateModel,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { BaseFormsListComponent, FormsConstants, FormsListComponentInstanceToken } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faEllipsisV, faPhone, faSync } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    defaultIfEmpty,
    firstValueFrom,
    forkJoin,
    interval,
    Observable,
    of,
    Subscription,
    switchMap,
    takeWhile,
    tap,
} from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, take } from 'rxjs/operators';
import { AlertService } from '../../core/services/eta-alert/alert.service';
import { OdataTravelStatusUpdateService } from '../travel-status-update/odata-travel-status-update.service';
import { TravelStatusUpdateComponent } from '../travel-status-update/travel-status-update.component';
import { ATS_FORM, STATUS_STEP_ACTION } from './status-step-action.enum';
import { StatusStepButtonModel } from './status-step-button.model';
import { Store } from '@ngrx/store';
import { getIsSAFormSubmitted } from 'libs/pace-forms/src/lib/store/selectors/form.selector';
import { loadSafetyAssessmentForm } from 'libs/pace-forms/src/lib/store/actions/safety-assessment.action';

@Component({
    selector: 'pace-status-step-button',
    templateUrl: './status-step-button.component.html',
    styleUrls: ['./status-step-button.component.scss'],
    standalone: false,
})
export class StatusStepButtonComponent implements OnInit, OnChanges, OnDestroy {
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);

    @Input()
    public notification: NotificationModel;
    @Input() public isDropup = false;

    // class members
    public model: StatusStepButtonModel;
    public stepEntries: Array<DisplayDecorator<TextModel>> = [];
    public readonly NOTIFICATION_STATUS_CODE = NOTIFICATION_STATUS_CODE;
    public readonly faPhone = faPhone;

    protected readonly faLoading = faSync;
    protected runAnimation = false;

    private travelStatusUpdateSubscription?: Subscription;
    private notificationSubscription?: Subscription;
    private displayEtaAlert = false;

    private readonly REMOTE_FIRST_STATUSES = new Set<NOTIFICATION_STATUS_CODE>([
        NOTIFICATION_STATUS_CODE.DISP,
        NOTIFICATION_STATUS_CODE.ETA,
        NOTIFICATION_STATUS_CODE.DURA,
        NOTIFICATION_STATUS_CODE.REMT,
    ]);

    private readonly ngrxEnabled = isNgRxEnabled();

    public faEllipsisV = faEllipsisV;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        @Inject(EscalationInstanceToken) private readonly escalationService: EscalationInterface,
        private readonly appNotificationService: AppNotificationService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly odataTravelStatusUpdateService: OdataTravelStatusUpdateService,
        private readonly synchronizationService: SynchronizationService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        private readonly alertService: AlertService,
        private readonly store: Store,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(FormsListComponentInstanceToken) private readonly formsListComponent: BaseFormsListComponent,
        @Inject(SoftwareLicencesInstanceToken) private readonly softwareLicencesService: SoftwareLicencesInterface,
        private readonly eventService: EventService,
    ) {
        this.displayEtaAlert = this.configService.getDisplayEtaAlert();
    }

    public ngOnInit(): void {
        const enableSAF = this.configService.getEnableSafetyAssessmentForm();
        const isATSStatusAvailable = this.notificationService
            .getAvailableStatus(this.notification)
            .includes(NOTIFICATION_STATUS_CODE.ATS);
        if (enableSAF && isATSStatusAvailable) {
            this.updateSafStatusIfSubmitted();
        }
    }

    public ngOnChanges(changeObj: SimpleChanges): void {
        if (changeObj.notification?.currentValue) {
            this.notificationSubscription?.unsubscribe(); // Unsubscribe if already active

            this.notificationSubscription = this.waitForNotificationReady$(
                changeObj.notification.currentValue,
            ).subscribe((ready) => {
                if (ready) {
                    this.reloadModel();
                }
            });
        }
    }

    /**
     * If Notification is not Remote support and FCC status is allowed then only last status should be dimmed
     * If Notification is remote work and FCC is allowed then bottom two status step are either TRAV or OSR
     * If Notification is not remote work and FCC is allowed then bottom two status step are ROSR and FCC
     * If Notification is not remote work and FCC is not allowed then last status is ROSR, and it should be dimmed
     * If bottom status are Escalate or Open forms or Short confirmation, then no need warning user
     *
     * @returns the dimmed status
     */
    public dimNonPriorityStatus(): ConfigConstants.DimStatus {
        const notificationType = this.notification.notificationType;
        const statusCode = this.notification.updatedStatus.statusCode;
        const showFCCStatus = this.configService.getShowFCCForNotificationTypes(notificationType);
        const enableRemoteWork = this.configService.getEnableRemoteWork();

        if (!this.notification.updatedStatus.isInProgress() && !this.notification.isRemoteSupport && !showFCCStatus) {
            return ConfigConstants.DimStatus.LastStatus;
        }

        if (enableRemoteWork && this.REMOTE_FIRST_STATUSES.has(statusCode)) {
            return ConfigConstants.DimStatus.LastTwoStatus;
        }

        return ConfigConstants.DimStatus.Default;
    }

    public ngOnDestroy(): void {
        if (typeof this.travelStatusUpdateSubscription !== 'undefined') {
            this.travelStatusUpdateSubscription.unsubscribe();
        }
        this.notificationSubscription?.unsubscribe();
    }

    private async checkNotificationStatusConcurrencyOutdated(notificationStatus: StatusModel): Promise<boolean> {
        // Check if the notification status was updated from a different device. If yes, stop the action and inform the user.
        const isButtonActionNotAllowed = await firstValueFrom(
            forkJoin({
                recentlyCompletedNotifications: this.notificationStatusService.loadRecentlyCompletedNotifications(),
                notificationStatusList: this.notificationStatusService.syncNotificationStatusList(),
            }).pipe(
                take(1),
                map(({ recentlyCompletedNotifications }) => {
                    const newStatus = this.notificationStatusService.getLatestNotificationStatusForId(
                        this.notification.identifier,
                    );
                    const wasRecentlyCompleted =
                        !!recentlyCompletedNotifications.completedNotifications[
                            this.notification.identifier.toString()
                        ];
                    return (
                        (newStatus !== undefined && notificationStatus.statusCode !== newStatus?.statusCode) ||
                        wasRecentlyCompleted
                    );
                }),
            ),
        );

        if (isButtonActionNotAllowed) {
            this.modalService.show(YesNoModalDialogComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_TITLE'),
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_BODY'),
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_ACTION'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });
            this.requestNotificationRefresh();
        }
        return isButtonActionNotAllowed;
    }

    /**
     * Asks for the notification list to be reloaded: a store dispatch once the notification domain
     * is served by the slice, and the `EventService` event the service behind
     * `NotificationDataInstanceToken` listens to while it is not.
     */
    private requestNotificationRefresh(): void {
        if (this.ngrxEnabled) {
            this.store.dispatch(notificationRefreshRequired());
            return;
        }
        this.eventService.triggerNotificationRefreshRequiredChanged(true);
    }

    public async onStepButtonClick(entry: DisplayDecorator<TextModel>): Promise<void> {
        this.runAnimation = true;

        try {
            const notificationStatus = this.notificationService.getStatus(this.notification);
            if (await this.checkNotificationStatusConcurrencyOutdated(notificationStatus)) {
                this.runAnimation = false;
                return;
            }

            if (
                notificationStatus.statusCode !== NOTIFICATION_STATUS_CODE.OSR &&
                !notificationStatus.isToBeSigned() &&
                this.notificationService.isAnyOtherNotificationInStatusDisp(this.notification) &&
                !(
                    entry.value === STATUS_STEP_ACTION.ETA ||
                    entry.value === STATUS_STEP_ACTION.CRM ||
                    entry.value === STATUS_STEP_ACTION.SCRM
                )
            ) {
                this.modalService.show(DashboardButtonComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        model: new DashboardButtonModel(
                            <string>this.translate.instant('DASHBOARD_NOTIFICATION_NEW_NOTIFICATION_WARNING_TITLE', {
                                status: entry.displayName,
                            }),
                            <string>this.translate.instant('DASHBOARD_NOTIFICATION_NEW_NOTIFICATION_WARNING_BODY'),
                        ),
                    },
                    ariaLabelledBy: 'dashboard-button-modal-title',
                });
                return;
            }

            if (
                this.configService.getShowRemoteStatusChangeWarning() &&
                this.configService.getEnableRemoteWork() &&
                this.showWarningForIncorrectStatusSelection(entry)
            ) {
                this.log.info(`onStepButtonClick: Show warning popup if user want to select ${entry.displayName}`);
                const selectedStatus =
                    entry.model.value === NOTIFICATION_STATUS_CODE.TRAV ||
                    entry.model.value === NOTIFICATION_STATUS_CODE.OSR
                        ? <string>this.translate.instant('NOTIFICATION_STATUS_ONSITE_LABEL')
                        : <string>this.translate.instant('NOTIFICATION_STATUS_REMOTE_LABEL');
                const notificationMarkedAs = this.notification.isRemoteSupport
                    ? <string>this.translate.instant('NOTIFICATION_STATUS_REMOTE_LABEL')
                    : <string>this.translate.instant('NOTIFICATION_STATUS_ONSITE_LABEL');
                const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                    backdrop: 'static',
                    class: 'ModalClass.MODAL_MEDIUM',
                    initialState: {
                        model: new YesNoModalDialogModel(
                            <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WORK_WARNING_LABEL'),
                            <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WORK_WARNING_BODY', {
                                planned: notificationMarkedAs,
                                startAs: selectedStatus,
                            }),
                            <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WOR_WARNING_YES_BUTTON'),
                            <string>(
                                this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WOR_WARNING_CANCEL_BUTTON')
                            ),
                        ),
                    },
                    ariaLabelledBy: 'yes-no-modal-title',
                });
                modalInstance.content?.modalResult.subscribe((modalResult: boolean) => {
                    if (modalResult) {
                        this.handleStatusAction(entry, notificationStatus);
                    }
                });
                return;
            }
            this.handleStatusAction(entry, notificationStatus);
        } finally {
            this.runAnimation = false;
        }
    }

    /**
     * Show warning message to user
     * If User status in NDP is REMT i.e. remote support and user selects OSR or TRAV from dropdown
     * If User status in NDP is not REMT and user selects Remote work or Remote support from dropdown
     *
     * @param entry is display decorate of Textmodel
     * @returns true if warning message should be shown
     * @private
     */
    private showWarningForIncorrectStatusSelection(entry: DisplayDecorator<TextModel>): boolean {
        return (
            (this.notification.isRemoteSupport &&
                this.notification.updatedStatus.statusCode !== NOTIFICATION_STATUS_CODE.TRAV &&
                (entry.model.value === NOTIFICATION_STATUS_CODE.OSR ||
                    entry.model.value === NOTIFICATION_STATUS_CODE.TRAV)) ||
            (!this.notification.isRemoteSupport &&
                (entry.model.value === NOTIFICATION_STATUS_CODE.FCC ||
                    entry.model.value === NOTIFICATION_STATUS_CODE.ROSR))
        );
    }

    private handleStatusAction(entry: DisplayDecorator<TextModel>, notificationStatus: StatusModel): void {
        const enableSoftwareLicences = this.clientManager.isNgmP58();

        switch (entry.value) {
            case STATUS_STEP_ACTION.ETA:
            case STATUS_STEP_ACTION.ROSR:
            case STATUS_STEP_ACTION.FCC:
            case STATUS_STEP_ACTION.TRAV:
            case STATUS_STEP_ACTION.OSR:
                // we need to load the software licences to have them available for offline usage
                // agreed with customer to load the licences on the OSR status
                // see ticket https://evorait.atlassian.net/browse/SHP-5638 for more details
                if (entry.value === STATUS_STEP_ACTION.OSR && enableSoftwareLicences) {
                    this.loadSoftwareLicences();
                }

                this.openTravelStatusUpdateModal(entry);
                break;
            case STATUS_STEP_ACTION.ATS:
                this.openSafetyAssessmentForm();
                break;
            case STATUS_STEP_ACTION.SOSR:
                // we need to load the software licences to have them available for offline usage
                // agreed with customer to load the licences on the SOSR status for PACE US MARS
                // see ticket https://evorait.atlassian.net/browse/SHP-5638 for more details
                if (enableSoftwareLicences) {
                    this.loadSoftwareLicences();
                }
                // SOSR: Special OSR for PACE US. In this case we skip opening the travel status update component since
                //       the user cannot change any of the dates or time anyway.
                this.openAcceptModal(entry);
                break;
            case STATUS_STEP_ACTION.CRM:
                this.openFullConfirmation();
                break;
            case STATUS_STEP_ACTION.SCRM:
                // Typical use-case where user was on notification-details screen already when it got cancelled, and then he attempts a short confirmation
                // Short confirmation is not allowed for cancelled notification so reroute to full confirmation instead
                if (
                    notificationStatus.isCancelled() ||
                    this.notificationService.isNotificationCancelled(this.notification) ||
                    this.notificationUpdateService.checkIfCancellationExists(this.notification.identifier)
                ) {
                    this.openFullConfirmation();
                    break;
                }
                this.openShortConfirmation();
                break;
            case STATUS_STEP_ACTION.ESCALATE:
                this.openEscalationModal();
                break;
            case STATUS_STEP_ACTION.FORMS:
                this.openPaceForms();
                break;
            default:
                this.log.warn(
                    `StatusStepButtonComponent : handleStatusAction: Cannot handle STATUS_STEP_ACTION ${entry.value}`,
                );
        }
    }

    private openFullConfirmation(): void {
        this.router
            .navigate([
                ConfigConstants.routeUrlConfirmation,
                this.notification.identifier.getFrontId(),
                this.notification.identifier.getQmnum(),
                this.notification.identifier.getManum(),
                CONFIRMATION_TYPES.NORMAL,
            ])
            .catch((error: Error) =>
                this.log.error(
                    'Error in StatusStepButtonComponent -> openFullConfirmation -> router.navigate : ',
                    error,
                ),
            );
    }

    private openShortConfirmation(): void {
        this.router
            .navigate([
                ConfigConstants.routeUrlConfirmation,
                this.notification.identifier.getFrontId(),
                this.notification.identifier.getQmnum(),
                this.notification.identifier.getManum(),
                CONFIRMATION_TYPES.SHORT,
            ])
            .catch((error: Error) =>
                this.log.error(
                    'Error in StatusStepButtonComponent -> openShortConfirmation -> router.navigate : ',
                    JSON.stringify(error),
                ),
            );
    }

    private openPaceForms(): void {
        const mainEquipment = this.notification.getMainEquipment();

        if (!mainEquipment) {
            throw new Error(
                `openPaceForns: No main equipment for notification ${this.notification.identifier.toString()}`,
            );
        }

        const formList = this.formsListComponent as unknown as new (...args: unknown[]) => BaseFormsListComponent;

        this.modalService.show(formList, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                notificationIdentifier: {
                    qmNum: this.notification.identifier.qmNum,
                    maNum: this.notification.identifier.maNum,
                    frontId: this.notification.identifier.frontId,
                },
                useFormListCache: false,
            } as BaseFormsListComponent,
            ariaLabelledBy: 'forms-list-modal-title',
        });
    }

    private reloadModel(currentStatus?: NOTIFICATION_STATUS_CODE): void {
        // recreate model for step button based on current status available from notification
        this.model = new StatusStepButtonModel(this.notification);

        if (currentStatus) {
            // select the current status for the dropdown
            this.model.actions.selectByValue(currentStatus);
        } else {
            this.model.actions.selectByIndex(0);
        }

        // remove the action that will be displayed in the main button from the dropdown list to avoid redundancy
        this.stepEntries = this.model.actions.value.filter(
            (entry) => entry.value !== this.model.actions.selectedEntry.value,
        );
    }

    private openEscalationModal(): void {
        const notificationTypeLabel = this.valueHelperService.getNotificationTypeLabel(
            this.notification.notificationType,
        );
        if (
            this.clientManager.isNgmP58() &&
            !this.configService.getEscalateNotificationTypes(this.notification.notificationType)
        ) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('NOTIFICATION_DETAILS_CONFIRM_TASK_ESCALATION_TITLE'),
                <string>this.translate.instant('NOTIFICATION_DETAILS_CONFIRM_TASK_ESCALATION_NOT_SUPPORTED', {
                    notificationType: notificationTypeLabel,
                }),
            );
            return;
        }
        const modalInstance = this.modalService.show(EscalationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                notification: this.notification,
            },
            ariaLabelledBy: 'escalation-modal-title',
        });
        modalInstance.content?.modalResult.subscribe((modalResult) => {
            if (modalResult) {
                // Once escalation is done, make the escalated button disabled
                this.model.isEscalated = true;
                // remove escalation timer only if its OSR is done
                this.escalationService.removeEscalationTimer(this.notification);
                if (this.displayEtaAlert) {
                    this.alertService.removeEtaTimer(this.notification);
                }
            }
        });
    }

    private openTravelStatusUpdateModal(entry: DisplayDecorator<TextModel>): void {
        const modalInstance = this.modalService.show(TravelStatusUpdateComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new TravelStatusUpdateModel(this.notification, <NOTIFICATION_STATUS_CODE>entry.value),
            },
            ariaLabelledBy: 'travel-status-update-modal-title',
        });

        this.travelStatusUpdateSubscription = modalInstance.content?.statusSubmitted.subscribe(
            (newStatus: NOTIFICATION_STATUS_CODE) => {
                this.reloadModel(newStatus);
                if (newStatus === NOTIFICATION_STATUS_CODE.OSR) {
                    this.escalationService.setTimerForEscalation(this.notification, moment.duration(0));
                    if (this.configService.getCentricarePCHEnabled()) {
                        this.eventService.triggerPCHAlertOnOSR();
                    }
                } else if (this.displayEtaAlert && newStatus === NOTIFICATION_STATUS_CODE.ETA) {
                    this.alertService.setTimerForEtaAlert(this.notification);
                } else if (this.displayEtaAlert && newStatus !== NOTIFICATION_STATUS_CODE.ETA) {
                    this.alertService.removeEtaTimer(this.notification);
                }
            },
        );
    }

    private openAcceptModal(entry: DisplayDecorator<TextModel>): void {
        const yesNoModal = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_SMALL,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('ACCEPT_NOTIFICATION_TITLE'),
                    <string>this.translate.instant('ACCEPT_NOTIFICATION_BODY'),
                    <string>this.translate.instant('MODAL_OK'),
                    <string>this.translate.instant('MODAL_CANCEL'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        const travelStatusUpdate = new TravelStatusUpdateModel(
            this.notification,
            <NOTIFICATION_STATUS_CODE>(<unknown>entry.value),
        );

        yesNoModal.content?.modalResult
            .pipe(
                take(1),
                filter((result: boolean) => result),
                mergeMap(() => this.odataTravelStatusUpdateService.saveStatus(travelStatusUpdate)),
                mergeMap(() => {
                    this.log.debug(
                        `StatusStepButtonComponent: Status updated successfully to ${travelStatusUpdate.nextStatus}`,
                    );
                    // save status in notification and localStorage
                    const statusModel = new StatusModel(
                        travelStatusUpdate.nextStatus,
                        travelStatusUpdate.date.value,
                        travelStatusUpdate.endDate.value,
                    );
                    return this.notificationService.saveStatus(travelStatusUpdate.notification, statusModel);
                }),
                mergeMap(() => {
                    this.reloadModel(travelStatusUpdate.nextStatus);
                    this.log.debug('StatusStepButtonComponent: submitted ETA status for PACE US -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(
                        `StatusStepButtonComponent: error in sendOfflineData after submitting ETA status for PACE US: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                        <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    public openSafetyAssessmentForm(): void {
        const { qmNum, maNum, frontId } = this.notification.identifier;
        const mainEquipment = this.notification.getMainEquipment();
        if (!isDefined(mainEquipment)) {
            throw new Error(`openSafetyAssessmentForm: No main Equipment available!`);
        }
        const formId = this.configService.getSafetyAssessmentForm();
        this.store.dispatch(
            loadSafetyAssessmentForm({
                notification: { qmNum, maNum, frontId },
                form: {
                    formId: formId,
                    equipmentId: mainEquipment?.equnr.value,
                    materialId: mainEquipment?.matnr.value,
                },
                status: FormsConstants.FORMSTATUS.LOCK,
            }),
        );
    }

    public disableButton(entry: TextModel): boolean {
        const isSAFFormEnabled = this.configService.getEnableSafetyAssessmentForm();
        const isOSR = entry.value === NOTIFICATION_STATUS_CODE.OSR;
        const isSAFFormSelected = this.model.actions.selectedEntry.displayName === ATS_FORM;

        return isSAFFormEnabled && isOSR && isSAFFormSelected;
    }

    private updateSafStatusIfSubmitted(): void {
        const safFormId = this.configService.getSafetyAssessmentForm();
        const currentNotificationNo = this.notification.identifier.value.NotificationNo.value as string;

        this.store
            .select(getIsSAFormSubmitted(safFormId, currentNotificationNo))
            .pipe(
                take(1),
                distinctUntilChanged(),
                filter((isSubmitted) => isSubmitted),
                switchMap(() => {
                    const now = moment();
                    const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.ATS, now, now);
                    return this.notificationService.saveStatus(this.notification, statusModel);
                }),
                tap(() => this.reloadModel(NOTIFICATION_STATUS_CODE.ATS)),
            )
            .subscribe();
    }

    private loadSoftwareLicences(): void {
        const mainEquipment = this.notification.getMainEquipment();
        if (!isDefined(mainEquipment)) {
            throw new Error(`StatusStepButtonComponent: loadSoftwareLicences: No main Equipment available!`);
        }
        this.softwareLicencesService.getSoftwareLicencesForEquipment(mainEquipment).pipe(take(1)).subscribe();
    }

    private waitForNotificationReady$(notification: NotificationModel): Observable<boolean> {
        const MAX_ATTEMPTS = 10;
        const INTERVAL_MS = 100;

        return interval(INTERVAL_MS).pipe(
            map(() => !!notification.updatedStatus && typeof notification.updatedStatus.isOnSiteWork === 'function'),
            takeWhile((isReady, index) => !isReady && index < MAX_ATTEMPTS, true),
            filter((isReady) => isReady),
            take(1),
            defaultIfEmpty(false),
        );
    }
}
